import {
  useAllFeatureFlags,
  useFeatureFlag,
} from '@ad-lucy/feature-flag-client-public';
import { LoaderCentered } from '@modules/components';
import NotFoundView from '@modules/components/NotFoundView';
import Layout from '@modules/Layout';
import { Acl } from '@modules/permissions/Acl';
import { lazy } from 'react';
import {
  createHashRouter,
  createRoutesFromElements,
  Navigate,
  Route,
  RouterProvider,
  useLocation,
} from 'react-router-dom';
import { SessionHandler } from './SessionHandler';

const FEATURE_FLAGGED_ROUTE_PREFIXES = [
  '/client-reporting',
  '/media-planning',
  '/analytics',
  '/assets',
  '/orders',
  '/document-management',
] as const;

function isFeatureFlaggedRoute(pathname: string): boolean {
  return FEATURE_FLAGGED_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

function CatchAllRoute() {
  const { isLoading: areFeatureFlagsLoading, features } = useAllFeatureFlags();
  const { pathname } = useLocation();
  const isAwaitingInitialFeatureFlags =
    areFeatureFlagsLoading && Object.keys(features).length === 0;

  if (isAwaitingInitialFeatureFlags && isFeatureFlaggedRoute(pathname)) {
    return <LoaderCentered />;
  }

  return <NotFoundView />;
}

const AssetsLibrary = lazy(() => import('../AssetsLibrary'));
const Campaign = lazy(() => import('@modules/Campaign'));
const CustomerModule = lazy(() => import('../Customers'));
const PreviewModule = lazy(() => import('../Preview'));
const MediaPlanning = lazy(() => import('../MediaPlanning'));
const AdCreator = lazy(() => import('../AdCreator'));
const SalesModule = lazy(() => import('../Sales'));
const Forecasts = lazy(() => import('../Booking/views/Forecasts'));
const BookNewCampaign = lazy(() => import('../Booking/views/BookNewCampaign'));
const PersonalSettings = lazy(() => import('../PersonalSettings'));
const OrganizationSettings = lazy(() => import('../OrganizationSettings'));
const Offer = lazy(() => import('@modules/Booking/views/Offer'));
const BookExistingCampaign = lazy(
  () => import('@modules/Booking/views/BookExistingCampaign'),
);
const Analytics = lazy(() => import('../Analytics'));
const ClientReporting = lazy(() => import('../ClientReporting'));
const Orders = lazy(() => import('../Orders'));
const DocumentManagement = lazy(() => import('../DocumentManagement'));

export function AppRouter() {
  const { enabled: isAssetsLibraryEnabled } = useFeatureFlag(
    'adc-mfe-assets-library',
  );
  const { enabled: isMediaPlanningEnabled } = useFeatureFlag(
    'mpfa-mfe-media-planning',
  );
  const { enabled: isAnalyticsEnabled } = useFeatureFlag(
    'reporting-analytics-mfe',
  );
  const { enabled: isClientReportingEnabled } = useFeatureFlag(
    'client-reporting-mfe',
  );
  const { enabled: isOrderManagementMfeEnabled } = useFeatureFlag(
    'order-management-mfe',
  );

  const routeObjects = createRoutesFromElements(
    <>
      <Route path="/campaigns/*" element={<Campaign mode={'campaigns'} />} />
      <Route
        path="/campaign/:campaignId/stage/booking/*"
        element={
          <Acl
            require={{
              or: [
                'booking:view',
                'campaign:booking',
                'campaign.preview.booking:view',
              ],
            }}
          >
            {/* there are other validations checked inside the BookExistingCampaign component other
      than the route guard used here. */}
            <BookExistingCampaign />
          </Acl>
        }
      />
      <Route
        path="/campaign/:campaignId/creative/:creativeId/stage/preview/*"
        element={
          <Acl require={['campaign.preview:view']}>
            <PreviewModule />
          </Acl>
        }
      />
      <Route
        path="/campaign/:campaignId/creative/:creativeId/stage/ad-creator/*"
        element={<AdCreator />}
      />
      <Route
        path="/campaign/:campaignId/stage/preview/*"
        element={
          <Acl require={['campaign.preview:view']}>
            <PreviewModule />
          </Acl>
        }
      />
      <Route path="/campaign/*" element={<Campaign mode={'campaign'} />} />
      <Route path="/personal-settings/*" element={<PersonalSettings />} />
      <Route
        path="/organization-settings/*"
        element={
          <Acl require={['admin:view']}>
            <OrganizationSettings />
          </Acl>
        }
      />
      {isAnalyticsEnabled && (
        <Route
          path="/analytics/*"
          element={
            <Acl require={['analytics:view']}>
              <Analytics />
            </Acl>
          }
        />
      )}
      <Route path="/showroom" element={<Campaign mode={'showroom'} />} />
      <Route
        path="showroom/:campaignId/*"
        element={
          <Acl require={['showroom:view']}>
            <PreviewModule isShowroom />
          </Acl>
        }
      />
      <Route
        path="/crm/*"
        element={
          <Acl require={['account:view']}>
            <CustomerModule />
          </Acl>
        }
      />
      {/* Redirect legacy offer-creation links to the sales board, so we don't break existing links. */}
      <Route
        path="/sales/offer-creation/*"
        element={
          <Acl require={['offer:view']}>
            <Navigate to="/sales/board" replace />
          </Acl>
        }
      />
      <Route
        path="/sales/*"
        element={
          <Acl require={['offer:view']}>
            <SalesModule />
          </Acl>
        }
      />
      {isMediaPlanningEnabled && (
        <Route
          path="/media-planning/*"
          element={
            <Acl require={['offer:view']}>
              <MediaPlanning />
            </Acl>
          }
        />
      )}
      <Route
        path="/offer/*"
        element={
          <Acl require={['offer.new:view']}>
            <Offer />
          </Acl>
        }
      />
      <Route
        path="/inventory-forecast/*"
        element={
          <Acl require={['potential:view']}>
            <Forecasts />
          </Acl>
        }
      />
      <Route
        path="/newcampaign/*"
        element={
          <Acl
            require={{
              or: [
                'booking:view',
                'campaign:booking',
                'campaign.preview.booking:view',
              ],
            }}
          >
            {/* there are other validations checked inside the BookNewCampaign component other
              than the route guard used here. */}
            <BookNewCampaign />
          </Acl>
        }
      />
      <Route path="/quickstart" element={<Campaign mode={'quickstart'} />} />
      <Route
        path="/"
        element={
          <Acl
            require={['quickstart:view']}
            fallback={<Navigate to="/campaigns" replace={true} />}
          >
            <Navigate to="/quickstart" replace={true} />
          </Acl>
        }
      />
      {isAssetsLibraryEnabled && (
        <Route path="/assets/*" element={<AssetsLibrary />} />
      )}
      {isClientReportingEnabled && (
        <Route path="/client-reporting/*" element={<ClientReporting />} />
      )}
      {isOrderManagementMfeEnabled && (
        <Route path="/orders/*" element={<Orders />} />
      )}
      {isOrderManagementMfeEnabled && (
        <Route path="/document-management/*" element={<DocumentManagement />} />
      )}
      <Route path="*" element={<CatchAllRoute />} />
    </>,
  );

  const router = createHashRouter([
    {
      element: (
        <SessionHandler>
          <Layout />
        </SessionHandler>
      ),
      children: routeObjects,
    },
  ]);

  return <RouterProvider router={router} />;
}
